import { Theme } from '@material-ui/core/styles';
import useTheme from '@material-ui/core/styles/useTheme';
import { DataResult, process, State } from '@progress/kendo-data-query';
import { getter } from '@progress/kendo-react-common';
import {
  getSelectedState,
  Grid,
  GridColumn,
  GridColumnProps,
  GridColumnReorderEvent,
  GridColumnResizeEvent,
  GridDataStateChangeEvent,
  GridDetailRowProps,
  GridExpandChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridRowProps,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import React, { useEffect, useState } from 'react';
import 'react-filter-box/lib/react-filter-box.css';
import { Option } from 'react-filter-box/src/GridDataAutoCompleteHandler';
import styled, { ThemeProvider } from 'styled-components';
import { ExportDTO, FilterBox, TableColumnId } from '../../store/table/types';
import ActionsCell from '../ActionsCell/ActionsCell';
import { Link } from '../ui';
import MyGridColumnMenu from './MyGridColumnMenu';
import './MyGridCore.styles.css';
import { gridSettingsProps } from './types';
// @ts-ignore
import ReactFilterBox, { Expression, SimpleResultProcessing } from 'react-filter-box';
import { getInitialDataState, getInitialFilterBox } from '../../store/table/slice';
import { useTranslation } from 'react-i18next';
import useDebounce from '../../hooks/useDebounce';
import { ExcelExport, ExcelExportColumn } from '@progress/kendo-react-excel-export';
import { FilterDescriptor } from '@progress/kendo-data-query/dist/npm/filtering/filter-descriptor.interface';
import { difference } from '../../helpers/set';
import { cleanDataState, preprocessDataState } from '../../store/table/helpers';

/**
 * Build options dynamically for react-filter-box based on grid settings.
 */
const initOptions = (gridSettings: gridSettingsProps): Option[] => {
  return gridSettings.gridColumnProps
    .filter((props) => {
      return 'filterable' in props ? props.filterable : true;
    })
    .map((props) => {
      let type = 'text';
      if (props.filter && props.filter == 'numeric') {
        type = 'numeric';
      }
      return {
        columnField: props.field,
        columnText: props.title ? props.title : props.field,
        type,
      };
    });
};

interface Props {
  allGridData: Record<string, unknown>[];
  gridSettings: gridSettingsProps;
  onGridSettingsChange?: (gridSettings: gridSettingsProps) => void;
  dataStateValue?: State;
  filterBoxValue?: FilterBox;
  selectedState: {
    [id: string]: boolean | number[];
  };
  handleSelectRow?: (data: { [p: string]: boolean | number[] }) => void;
  onDataStateChange?: (dataState: State) => void;
  onFilterBoxChange?: (filterBox: FilterBox) => void;
  dateFormat: string | null;
  detail?: React.ComponentType<GridDetailRowProps> | null;
  onExpandChange?: (event: GridExpandChangeEvent) => void;
  selectable: boolean;
  scrollToSelectedRow?: number;
  showActionsColumn: boolean;
  showFilters: boolean;
  columnsAlwaysShown?: TableColumnId[];
  filteredIds?: number[];
  onFilteredIds?: (ids: number[]) => void;
  externalChangeCount?: number;
  exportDTO?: ExportDTO;
}

const MyGridCore: React.FC<Props> = ({
  allGridData,
  gridSettings,
  onGridSettingsChange,
  dataStateValue,
  filterBoxValue,
  selectedState,
  handleSelectRow,
  onDataStateChange,
  onFilterBoxChange,
  dateFormat,
  detail,
  onExpandChange,
  selectable,
  scrollToSelectedRow,
  showActionsColumn,
  showFilters,
  columnsAlwaysShown,
  filteredIds,
  onFilteredIds,
  externalChangeCount,
  exportDTO,
}) => {
  const { t, i18n } = useTranslation();
  const _export = React.useRef<ExcelExport | null>(null);

  const scrollTo = (rowIndex: number) => {
    if (gridRef.current) {
      gridRef.current.scrollIntoView({
        rowIndex: rowIndex,
      });
    }
  };

  const excelExport = () => {
    if (_export && _export.current !== null && exportDTO) {
      const { arrangeData, rowsOption } = exportDTO;

      let data: any[];
      if (rowsOption == 'all') {
        const { skip, take, ...rest } = internalDataState; // skip pagination
        data = filterData(allGridData, filterBox.expressions, rest).data;
      } else if (rowsOption == 'selected') {
        const selected = Object.keys(selectedState)
          .filter((key) => selectedState[key])
          .map((key) => parseInt(key));
        data = allGridData.filter((row) => selected.includes(idGetter(row)));
      } else {
        data = [];
      }
      _export.current.save(arrangeData ? arrangeData(data) : data, gridColumnsProps);
    }
  };

  /**
   * Applies filters on data
   * @param data
   * @param expressions
   * @param dataState
   */
  const filterData = (data: Record<string, unknown>[], expressions: Expression[], dataState: State): DataResult => {
    const newData = new SimpleResultProcessing(options).process(data, expressions);
    return process(newData, dataState);
  };

  const updateDataResult = (dataState: State) => {
    const { skip, take, ...rest } = dataState;
    const filtered = filterData(allGridData, filterBox.expressions, rest);
    if (onFilteredIds) {
      const ids = filtered.data.map((row) => idGetter(row));
      if (ids.toString() != filteredIds?.toString()) {
        onFilteredIds(ids);

        /* Only take into account those selected rows that pass table filters */
        const selected = Object.keys(selectedState)
          .filter((key) => selectedState[key])
          .map((key) => parseInt(key))
          .filter((id) => ids.includes(id));
        selectRowsBulk(selected);
      }
    }
    return process(filtered.data, { skip, take });
  };

  const doFilter = (dataState: State) => {
    setDataResult(updateDataResult(dataState));

    // Sync redux externally
    if (onDataStateChange) {
      onDataStateChange(cleanDataState(dataState));
    }
  };

  const idGetter = getter(gridSettings.DATA_ITEM_KEY);

  const initDataState = (): State =>
    preprocessDataState(
      dataStateValue ?? getInitialDataState(),
      gridSettings.gridColumnProps.filter((props) => props.filter == 'date').map((props) => props.field),
    );

  /* hooks */
  const [internalDataState, setInternalDataState] = useState<State>(initDataState);
  const [options] = useState<Option[]>(() => initOptions(gridSettings));
  const gridRef = React.useRef<any>(null);
  const filterBox = filterBoxValue ?? getInitialFilterBox();
  const [dataResult, setDataResult] = useState<DataResult>({ data: [], total: 0 });
  const [scroll, setScroll] = useState(scrollToSelectedRow);
  const debounce = useDebounce();
  const selectionChange = React.useCallback(
    (event: GridSelectionChangeEvent) => {
      const newSelectedState = getSelectedState({
        event,
        selectedState: selectedState,
        dataItemKey: gridSettings.DATA_ITEM_KEY,
      });
      handleSelectRow?.(newSelectedState);
    },
    [selectedState],
  );
  const headerSelectionChange = React.useCallback((event: GridHeaderSelectionChangeEvent) => {
    const checkboxElement: any = event.syntheticEvent.target;
    const checked = checkboxElement.checked;
    const newSelectedState = {};

    event.dataItems.forEach((item) => {
      // @ts-ignore
      newSelectedState[idGetter(item)] = checked;
    });
    handleSelectRow?.(newSelectedState);
  }, []);

  useEffect(() => {
    if (scroll && dataResult.data.length) {
      // Find the position in the array of the given ID and add 1 to the position
      const rowIndex = dataResult.data.findIndex((element) => idGetter(element) === scrollToSelectedRow) + 1;
      scrollTo(rowIndex);
      setScroll(undefined); // Avoid to scroll more than once
    }
  }, [dataResult]);

  useEffect(() => {
    setDataResult(updateDataResult(internalDataState));
  }, [allGridData]);

  /* This effect synchronizes with changes in filters from outside the component */
  useEffect(() => {
    if (dataStateValue && externalChangeCount) {
      handleDataStateChange(initDataState(), 0);
    }
  }, [externalChangeCount]);

  /* This useEffect is an ugly way to trigger a excel file import */
  useEffect(() => {
    if (exportDTO?.count) {
      excelExport();
    }
  }, [exportDTO?.count]);

  const getFiltersByField = (state: State): { [key: string]: FilterDescriptor } => {
    const filtersByField: { [key: string]: FilterDescriptor } = {};

    (state?.filter?.filters ?? []).forEach((filter) => {
      filter = filter as FilterDescriptor;
      if (Array.isArray(filter.value)) {
        filter.value.forEach((value) => {
          // @ts-ignore
          filtersByField[`${filter.field}-${value.id}`] = filter;
        });
      } else {
        filtersByField[`${filter.field}-${filter.value}`] = filter;
      }
    });

    return filtersByField;
  };

  const getFilterChange = (newDataState: State): [FilterDescriptor | undefined, boolean | undefined] => {
    const currentFilters = getFiltersByField(internalDataState);
    const newFilters = getFiltersByField(newDataState);

    /* Track which filter changed */
    let diff: Set<string>;
    let field: string;
    let filter: FilterDescriptor | undefined;
    let added: boolean | undefined;
    if ((diff = difference(new Set(Object.keys(currentFilters)), new Set(Object.keys(newFilters)))).size) {
      // Filter was removed
      field = Array.from(diff)[0]; // diff must contain 1 element because user changes 1 filter at a time.
      filter = currentFilters[field];
      added = false;
    } else if ((diff = difference(new Set(Object.keys(newFilters)), new Set(Object.keys(currentFilters)))).size) {
      // Filter was added
      field = Array.from(diff)[0]; // diff must contain 1 element because user changes 1 filter at a time.
      filter = newFilters[field];
      added = true;
    }

    return [filter, added];
  };

  const needDebounce = (newDataState: State): boolean => {
    const [filter] = getFilterChange(newDataState);
    return filter !== undefined && typeof filter.operator == 'string';
  };

  const handleDataStateChange = (newDataState: State, msecs = 300) => {
    // Update internally
    setInternalDataState(newDataState);

    // Sync redux externally
    debounce(() => doFilter(newDataState), msecs);
  };

  const columnReorderChange = (event: GridColumnReorderEvent) => {
    const orderIndexByColumnId: { [key: string]: number } = {};
    event.columns.forEach((c) => {
      // @ts-ignore
      orderIndexByColumnId[c.id] = c.orderIndex;
    });

    const newGridColumnProps: GridColumnProps[] = gridSettings.gridColumnProps.map((c) => {
      return {
        ...c,
        orderIndex: orderIndexByColumnId[c.id] ?? c.orderIndex,
      };
    });

    const newGridSettings = {
      ...gridSettings,
      gridColumnProps: newGridColumnProps,
    };

    onGridSettingsChange?.(newGridSettings);
  };

  const columnResizeChange = (event: GridColumnResizeEvent) => {
    if (!event.end) return;

    const newGridColumnProps = gridSettings.gridColumnProps.map((column) => {
      if (column.id === event.targetColumnId) {
        return { ...column, width: event.newWidth + 'px' };
      } else {
        return column;
      }
    });

    const newGridSettings = {
      ...gridSettings,
      gridColumnProps: newGridColumnProps,
    };

    onGridSettingsChange?.(newGridSettings);
  };

  const onLockedColumnChange = (value: string[]) => {
    const newGridColumnProps = gridSettings.gridColumnProps.map((c) => {
      return {
        ...c,
        locked: value.includes(c.id),
      };
    });

    const newGridSettings = {
      ...gridSettings,
      gridColumnProps: newGridColumnProps,
    };

    onGridSettingsChange?.(newGridSettings);
  };

  const onHiddenColumnChange = (columnId: string) => {
    const newGridColumnProps = gridSettings.gridColumnProps.map((c) => {
      if (c.id === columnId) {
        return { ...c, orderIndex: c.orderIndex > 0 ? 0 : c.orderIndex };
      } else {
        return c;
      }
    });

    const newGridSettings = {
      ...gridSettings,
      gridColumnProps: newGridColumnProps,
    };

    onGridSettingsChange?.(newGridSettings);
  };

  function onParseOk(expressions: Expression[]) {
    // Set skip to 0 in order to move pagination to the first page
    const newDataState = {
      ...internalDataState,
      skip: 0,
    };
    const newFilterBox = { ...filterBox, expressions };
    onFilterBoxChange?.(newFilterBox);
    handleDataStateChange(newDataState);
  }

  const selectRowsBulk = (rowIds: number[]) => {
    const newSelectedState: {
      [id: string]: boolean | number[];
    } = {};
    rowIds.forEach((id) => {
      newSelectedState[id] = true;
    });
    handleSelectRow?.(newSelectedState);
  };

  const lockColumn = (colId: string) => {
    const locked = gridSettings.gridColumnProps
      .filter((c) => {
        if (c.id == colId) {
          return !c.locked;
        }
        return c.locked;
      })
      .map((c) => c.id);
    onLockedColumnChange(locked);
  };

  const getMaxOrderIndex = (): number => {
    return Math.max(...gridSettings.gridColumnProps.map((columnProp) => columnProp.orderIndex));
  };

  const headerSelectionValue = dataResult.data.findIndex((item) => !selectedState[idGetter(item)]) === -1;

  let selectAllRowsButton;
  if (
    headerSelectionValue &&
    internalDataState.take &&
    dataResult.total > internalDataState.take &&
    Object.keys(selectedState).length !== dataResult.total
  ) {
    selectAllRowsButton = (
      <div className="flex">
        <p className="">
          All <b>{internalDataState.take}</b> rows on this page are selected.&nbsp;
        </p>
        <Link
          className="cursor-pointer"
          onClick={() => {
            const { skip, take, ...rest } = internalDataState;
            const result = filterData(allGridData, filterBox.expressions, rest);
            const rowIds = result.data.map((item) => idGetter(item));
            selectRowsBulk(rowIds);
          }}
        >
          Select all {dataResult.total} rows in the table
        </Link>
      </div>
    );
  }

  /* Dynamically create grid columns based on the given configuration */
  let selectableProps = {};
  if (selectable) {
    selectableProps = {
      selectedField: gridSettings.SELECTED_FIELD,
      selectable: { enabled: true, mode: 'multiple', drag: false, cell: false },
    };
  }

  const gridColumnsProps = gridSettings.gridColumnProps
    .filter((props) => (props.orderIndex != undefined ? props.orderIndex > 0 : true))
    .sort((a, b) => {
      return a.orderIndex - b.orderIndex;
    });

  /*
   * Prepare data for Grid.
   */
  const data = dataResult.data.map((item, index) => ({
    ...item,
    [gridSettings.SELECTED_FIELD]: selectedState[idGetter(item)],
  }));

  const theme = useTheme();

  const grid = (
    <StyledGridWrapper className="flex h-full">
      <StyledGrid className="k-grid" style={{ height: '100%', flexGrow: 1, flexShrink: 1, border: 0 }}>
        <ExcelExport ref={_export}>
          {gridColumnsProps.map((column, index) => (
            <ExcelExportColumn key={index} {...column} />
          ))}
          <Grid
            sortable={{ mode: 'multiple' }}
            filterable={showFilters} //{!gridSettings.advancedFilter}
            groupable={false}
            reorderable={true}
            resizable={true}
            total={dataResult.total}
            pageable={{
              buttonCount: 8,
              pageSizes: [10, 20, 50, 100],
            }}
            data={data}
            detail={detail}
            onExpandChange={onExpandChange}
            style={{
              height: '10vh',
              flexGrow: 1,
              flexShrink: 1,
              border: 0,
              fontSize: `${gridSettings.fontSize}px`,
            }}
            dataItemKey={gridSettings.DATA_ITEM_KEY}
            {...selectableProps}
            {...internalDataState}
            onSelectionChange={selectionChange}
            onHeaderSelectionChange={headerSelectionChange}
            onDataStateChange={(event: GridDataStateChangeEvent) => handleDataStateChange(event.dataState)}
            onColumnReorder={columnReorderChange}
            onColumnResize={columnResizeChange}
            rowRender={rowRender(theme)}
            className="sggrid"
            ref={gridRef}
          >
            {selectable && (
              <GridColumn
                field={gridSettings.SELECTED_FIELD}
                width="50px"
                headerSelectionValue={headerSelectionValue}
                filterable={false}
                reorderable={false}
                resizable={false}
                key="selected"
                orderIndex={0}
                locked={true}
                className="gcgrid"
              />
            )}
            {gridColumnsProps.map((props) => {
              const newProps = { ...props };

              // If filter is not defined set 'text' as default
              if (!newProps.filter) {
                newProps.filter = 'text';
              }

              // Decide here what format to use when displaying the date field.
              if (newProps.filter === 'date') {
                newProps.format = `{0:${dateFormat || 'dd/MM/y HH:mm'}}`;
              }

              return (
                <GridColumn
                  key={props.id}
                  {...newProps}
                  hidden={false}
                  columnMenu={
                    onGridSettingsChange
                      ? (columnMenuProps) =>
                          MyGridColumnMenu(
                            columnMenuProps,
                            props.locked,
                            props.id,
                            lockColumn,
                            columnsAlwaysShown?.includes(props.id) ? undefined : onHiddenColumnChange,
                          )
                      : undefined
                  }
                  className="gcgrid"
                />
              );
            })}
            {showActionsColumn && (
              <GridColumn
                key="row_actions"
                id="row_actions"
                field="row_actions"
                width="100px"
                title={t('Actions')}
                orderIndex={getMaxOrderIndex() + 1}
                cell={ActionsCell}
                filterable={false}
                reorderable={false}
                resizable={false}
                locked={true}
                className="gcgrid"
              />
            )}
          </Grid>
        </ExcelExport>
      </StyledGrid>
    </StyledGridWrapper>
  );

  return (
    <>
      <ThemeProvider theme={theme}>
        <div className="flex flex-col h-full z-40" style={{ flexGrow: 1 }}>
          {selectAllRowsButton}
          {gridSettings.advancedFilter && (
            <div>
              <span>Advanced Filter</span>
              <ReactFilterBox
                query={filterBox.query}
                options={options}
                onParseOk={onParseOk}
                onChange={(query: string) => onFilterBoxChange?.({ ...filterBox, query })}
              />
            </div>
          )}
          {grid}
        </div>
      </ThemeProvider>
    </>
  );
};

const grid_bg = (props: any) => props.theme.palette.background.default;
const grid_alt_bg = (props: any) => props.theme.palette.primary.main;
const grid_border = (props: any) => props.theme.palette.primary.main;
const grid_header_border = (props: any) => props.theme.palette.secondary.light;
const grid_header_text = (props: any) => props.theme.palette.text.primary;
const grid_header_background = (props: any) => props.theme.palette.secondary.dark;
const grid_sticky_header_text = (props: any) => props.theme.palette.text.primary;
const grid_sticky_header_bg = (props: any) => props.theme.palette.secondary.dark;
const grid_sticky_header_border = (props: any) => props.theme.palette.secondary.light;

const grid_grouping_row_text = (props: any) => props.theme.palette.primary.main;
const grid_grouping_row_bg = (props: any) => props.theme.palette.primary.main;
const grid_text = (props: any) => props.theme.palette.primary.main;
const grid_hovered_text = (props: any) => props.theme.palette.primary.main;
const grid_selected_text = (props: any) => props.theme.palette.secondary.main;
const grid_selected_bg = (props: any) => props.theme.palette.secondary.light;
const grid_focused_shadow = (props: any) => props.theme.palette.primary.main;
const grid_sticky_border = (props: any) => 'rgba(0, 0, 0, 0.08);'; //props.theme.palette.background.paper; //

const grid_hovered_bg = (props: any) => props.theme.palette.primary.light;
const grid_sticky_hovered_bg = (props: any) => props.theme.palette.primary.light;
const grid_sticky_bg = (props: any) => props.theme.palette.background.paper;

const grid_sticky_alt_bg = (props: any) => props.theme.palette.background.paper;
const grid_sticky_selected_bg = (props: any) => props.theme.palette.secondary.light;

const grid_sticky_footer_bg = (props: any) => props.theme.palette.primary.main;
const grid_sticky_selected_alt_bg = (props: any) => props.theme.palette.secondary.light;
const grid_sticky_selected_hovered_bg = (props: any) => props.theme.palette.primary.light;
const grid_sorted_bg = (props: any) => props.theme.palette.secondary.dark;
const grid_sorted_text = (props: any) => props.theme.palette.primary.main;
const grid_sorting_indicator_text = (props: any) => props.theme.palette.text.primary;
const button_hovered_text = (props: any) => props.theme.palette.primary.main;
const button_hovered_bg = (props: any) => props.theme.palette.primary.main;
const selected_text = (props: any) => props.theme.palette.primary.main;
const selected_bg = (props: any) => props.theme.palette.primary.main;

const grid_footer_text = (props: any) => props.theme.palette.primary.main;
const grid_footer_bg = (props: any) => props.theme.palette.secondary.main;
const grid_footer_border = (props: any) => props.theme.palette.primary.main;

const list_item_text = (props: any) => props.theme.palette.primary.main;
const list_item_bg = (props: any) => props.theme.palette.primary.main;
const list_item_hovered_text = (props: any) => props.theme.palette.primary.main;
const list_item_hovered_bg = (props: any) => props.theme.palette.primary.main;
const list_item_focused_shadow = (props: any) => props.theme.palette.primary.main;
const list_item_selected_text = (props: any) => props.theme.palette.primary.main;
const list_item_selected_bg = (props: any) => props.theme.palette.primary.main;

const popup_text = (props: any) => props.theme.palette.primary.main;
const popup_bg = (props: any) => props.theme.palette.primary.main;
const popup_border = (props: any) => props.theme.palette.primary.main;

const grid_column_menu_group_header_text = (props: any) => props.theme.palette.primary.main;
const grid_column_menu_group_header_bg = (props: any) => props.theme.palette.primary.main;
const grid_column_menu_group_header_border = (props: any) => props.theme.palette.primary.main;

const rowRender = (theme: Theme) => (trElement: React.ReactElement<HTMLTableRowElement>, props: GridRowProps) => {
  const selected = props.dataItem.selected;
  const alt = false; // trElement.props.className.includes('k-alt');

  const bgcolor = alt ? theme.palette.background.default : theme.palette.background.paper;
  let mystyle = {};
  if (selected) {
    mystyle = {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.secondary.light,
    };
  } else {
    mystyle = { color: theme.palette.text.primary, backgroundColor: bgcolor };
  }

  const trProps: any = {
    style: mystyle,
  };
  return React.cloneElement(trElement, { ...trProps }, trElement.props.children);
};

export const StyledGrid = styled.div`
  .k-grid td.k-state-selected,
  .k-grid tr.k-state-selected > td.gcgrid {
    /*background-color: yellow;*/
  }

  .k-grid-header,
  .k-header,
  th.k-header,
  .k-grid-header-wrap,
  .k-grouping-header,
  .k-grouping-header .k-group-indicator,
  .k-grid td,
  .k-grid-footer,
  .k-grid-footer-wrap,
  .k-grid-content-locked,
  .k-grid-footer-locked,
  .k-grid-header-locked,
  .k-filter-row > td,
  .k-filter-row > th {
    border-color: ${(props) => grid_header_border(props)};
  }

  .k-grid-header,
  .k-grouping-header,
  .k-grid-add-row,
  .k-grid-footer {
    color: ${(props) => grid_header_text(props)};
    background-color: ${(props) => grid_header_background(props)};
    font-weight: bold;
  }

  .k-grid-content {
    // setting this background color resolves glitches in iOS
    background-color: ${(props) => grid_bg(props)};
  }

  .k-group-footer td,
  .k-grouping-row td,
  tbody .k-group-cell {
    color: ${(props) => grid_grouping_row_text(props)};
    background-color: ${(props) => grid_grouping_row_bg(props)};
  }

  .k-grouping-dropclue {
    &::before {
      border-color: ${(props) => grid_header_text(props)} transparent transparent;
    }

    &::after {
      background-color: ${(props) => grid_header_text(props)};
    }
  }

  .k-grid-table .k-grid tbody tr:hover,
  .k-grid-table .k-grid tbody tr.k-state-hover {
    background-color: red;
  }

  .k-grid-table td a {
    color: ${(props) => grid_text(props)};
    text-decoration: none;
    cursor: pointer;
    text-align: center;
    /* padding: 1rem 0; */
    display: block;
    align-content: center;
  }
  .k-grid {
    color: ${(props) => grid_text(props)};
    background-color: ${(props) => grid_bg(props)};

    tr.k-alt {
      background-color: ${(props) => grid_alt_bg(props)};
    }

    tbody tr:hover,
    tbody tr.k-state-hover {
      color: ${(props) => grid_hovered_text(props)};
      background-color: '#ff0000'; //${(props) => grid_hovered_bg(props)};
    }

    td.k-state-selected,
    tr.k-state-selected > td {
      color: ${(props) => grid_selected_text(props)};
      background-color: ${(props) => grid_selected_bg(props)};
    }

    // Focused state
    td.k-state-focused,
    th.k-state-focused,
    th:focus,
    .k-master-row > td:focus,
    .k-grouping-row > td:focus,
    .k-detail-row > td:focus,
    .k-group-footer > td:focus,
    .k-grid-pager.k-state-focused {
      box-shadow: ${(props) => grid_focused_shadow(props)};
    }

    .k-grid-filter,
    .k-header-column-menu,
    .k-hierarchy-cell .k-icon {
      color: ${(props) => grid_header_text(props)};
    }

    .k-grouping-row {
      background-color: ${(props) => grid_grouping_row_bg(props)};

      .k-icon {
        color: ${(props) => grid_header_text(props)};
      }

      .k-grid-content-sticky {
        border-color: ${(props) => grid_sticky_border(props)};
        border-top-color: ${(props) => grid_header_border(props)};
      }
    }
    // Locked columns
    .k-grid-header-locked,
    .k-grid-content-locked,
    .k-grid-header-locked .k-header,
    .k-grid-content-locked td {
      border-color: ${(props) => grid_sticky_header_border(props)};
    }

    .k-grid-content-locked {
      .k-group-footer td,
      .k-group-cell {
        border-color: ${(props) => grid_header_border(props)};
      }

      .k-grouping-row + tr td {
        border-top-color: ${(props) => grid_header_border(props)};
      }

      // Selected state
      .k-state-selected td {
        background-color: ${(props) => grid_sticky_selected_bg(props)};
      }

      .k-state-selected.k-alt td {
        background-color: ${(props) => grid_sticky_selected_alt_bg(props)};
      }

      // Hovered state
      .k-state-hover td,
      tr:hover td {
        background-color: ${(props) => grid_sticky_hovered_bg(props)};
      }

      // Selected hover
      .k-state-selected:hover td,
      .k-state-selected.k-state-hover td {
        background-color: ${(props) => grid_sticky_selected_hovered_bg(props)};
      }
    }
    .k-grid-header-locked .k-header {
      border-bottom-color: ${(props) => grid_header_border(props)};
    }
  }
  col.k-sorted,
  th.k-sorted {
    background-color: ${(props) => grid_sorted_bg(props)};
    color: ${(props) => grid_sorted_text(props)};
    .k-icon {
      color: ${(props) => grid_header_text(props)};
    }
  }
  [type='checkbox']:checked {
    background-image: none;
    background-color: ${(props) => selected_text(props)};
    appearance: none;
  }
  [type='checkbox']:focus {
    box-shadow: none;
  }

  // Grid header
  .k-grid-header {
    .k-i-sort-asc-sm,
    .k-i-sort-desc-sm,
    .k-sort-order {
      color: ${(props) => grid_sorting_indicator_text(props)};
    }

    .k-grid-filter,
    .k-header-column-menu,
    .k-hierarchy-cell .k-icon {
      &:hover {
        color: ${(props) => button_hovered_text(props)};
        background-color: ${(props) => button_hovered_bg(props)};
      }
      &:focus,
      &.k-state-focus,
      &.k-state-border-down {
        box-shadow: inset 0 0 0 2px rgba(0, 0, 0, 0.1);
      }
      &.k-state-active {
        color: ${(props) => selected_text(props)};
        background-color: ${(props) => selected_bg(props)};
      }
    }

    th.k-grid-header-sticky,
    td.k-grid-header-sticky {
      color: ${(props) => grid_sticky_header_text(props)};
      background-color: ${(props) => grid_sticky_header_bg(props)};

      border-right-color: ${(props) => grid_sticky_header_border(props)};
      border-left-color: ${(props) => grid_sticky_header_border(props)};
    }

    .k-grid-header-sticky.k-sorted {
      color: ${(props) => grid_sorted_text(props)};
      background-color: ${(props) => grid_sticky_header_bg(props)};

      border-right-color: ${(props) => grid_sticky_header_border(props)};
      border-left-color: ${(props) => grid_sticky_header_border(props)};
    }
  }

  // Grid footer
  .k-grid-footer {
    color: ${(props) => grid_footer_text(props)};
    background-color: ${(props) => grid_footer_bg(props)};
    border-color: ${(props) => grid_footer_border(props)};

    .k-grid-footer-sticky {
      border-left-color: ${(props) => grid_sticky_border(props)};
      border-right-color: ${(props) => grid_sticky_border(props)};
      background-color: ${(props) => grid_sticky_footer_bg(props)};
    }
  }

  .k-master-row {
    .k-grid-content-sticky {
      border-color: ${(props) => grid_sticky_border(props)};
      border-top-color: ${(props) => grid_header_border(props)};
      background-color: ${(props) => grid_sticky_bg(props)};
    }

    .k-grid-row-sticky {
      border-top-color: ${(props) => grid_sticky_border(props)};
      border-bottom-color: ${(props) => grid_sticky_border(props)};
      background-color: ${(props) => grid_sticky_bg(props)};
    }

    &.k-alt {
      .k-grid-content-sticky,
      .k-grid-row-sticky {
        background-color: ${(props) => grid_sticky_alt_bg(props)};
      }
    }

    // Selected state
    &.k-state-selected .k-grid-content-sticky,
    &.k-state-selected .k-grid-row-sticky,
    td.k-grid-content-sticky.k-state-selected {
      background-color: ${(props) => grid_sticky_selected_bg(props)};
    }

    &.k-state-selected.k-alt .k-grid-content-sticky,
    &.k-state-selected.k-alt .k-grid-row-sticky,
    &.k-alt td.k-grid-content-sticky.k-state-selected {
      background-color: ${(props) => grid_sticky_selected_alt_bg(props)};
    }

    // Hovered state
    &:hover td.gcgrid,
    &:hover .k-grid-content-sticky,
    &:hover .k-grid-row-sticky,
    &.k-state-hover .k-grid-content-sticky,
    &.k-state-hover .k-grid-row-sticky {
      background-color: ${(props) => grid_sticky_hovered_bg(props)};
    }

    // Selected hover
    &.k-state-selected:hover .k-grid-content-sticky,
    &.k-state-selected:hover .k-grid-row-sticky,
    &.k-state-selected.k-state-hover .k-grid-content-sticky,
    &.k-state-selected.k-state-hover .k-grid-row-sticky,
    &:hover td.k-grid-content-sticky.k-state-selected,
    &.k-state-hover td.k-grid-content-sticky.k-state-selected {
      background-color: ${(props) => grid_sticky_selected_hovered_bg(props)};
    }
  }

  kendo-grid {
    .k-grid-content-sticky {
      border-top-color: ${(props) => grid_header_border(props)};
      border-left-color: ${(props) => grid_sticky_border(props)};
      border-right-color: ${(props) => grid_sticky_border(props)};
      background-color: ${(props) => grid_sticky_bg(props)};

      &:hover,
      &.k-state-hover {
        background-color: ${(props) => grid_sticky_hovered_bg(props)};
      }
    }

    .k-grid-row-sticky {
      td {
        border-top-color: ${(props) => grid_sticky_border(props)};
        border-bottom-color: ${(props) => grid_sticky_border(props)};
        background-color: ${(props) => grid_sticky_bg(props)};
      }

      &:hover td,
      &.k-state-hover td {
        background-color: ${(props) => grid_sticky_hovered_bg(props)};
      }
    }

    .k-alt .k-grid-content-sticky,
    .k-grid-row-sticky.k-alt td {
      background-color: ${(props) => grid_sticky_alt_bg(props)};
    }

    // Selected state
    tr.k-state-selected .k-grid-content-sticky,
    .k-state-selected.k-grid-row-sticky td,
    .k-grid-row-sticky td.k-state-selected,
    .k-state-selected.k-grid-content-sticky {
      @include fill($bg: $grid-sticky-selected-bg);
      background-color: ${(props) => grid_sticky_selected_bg(props)};
    }

    tr.k-state-selected.k-alt .k-grid-content-sticky,
    .k-state-selected.k-alt.k-grid-row-sticky td,
    .k-alt .k-state-selected.k-grid-content-sticky {
      background-color: ${(props) => grid_sticky_selected_alt_bg(props)};
    }

    // Hover state
    tr:hover .k-grid-content-sticky,
    tr.k-state-hover .k-grid-content-sticky,
    .k-grid-row-sticky:hover td,
    .k-grid-row-sticky.k-state-hover td,
    .k-grid-row-sticky.k-alt:hover td,
    .k-grid-row-sticky.k-alt.k-state-hover td,
    .k-alt:hover .k-grid-content-sticky,
    .k-alt.k-state-hover .k-grid-content-sticky {
      background-color: ${(props) => grid_sticky_hovered_bg(props)};
    }

    // Selected + Hover
    tr.k-state-selected:hover .k-grid-content-sticky,
    tr.k-state-selected.k-state-hover .k-grid-content-sticky,
    .k-state-selected.k-grid-row-sticky:hover td,
    .k-state-selected.k-grid-row-sticky.k-state-hover td,
    .k-state-selected.k-alt.k-grid-row-sticky:hover td,
    .k-state-selected.k-alt.k-grid-row-sticky.k-state-hover td,
    tr.k-state-selected.k-alt:hover .k-grid-content-sticky,
    tr.k-state-selected.k-alt.k-state-hover .k-grid-content-sticky,
    .k-grid-row-sticky:hover td.k-state-selected,
    .k-grid-row-sticky.k-state-hover td.k-state-selected,
    tr:hover .k-grid-content-sticky.k-state-selected,
    tr.k-state-hover .k-grid-content-sticky.k-state-selected {
      background-color: ${(props) => grid_sticky_selected_hovered_bg(props)};
    }
  }

  .k-grouping-row {
    .k-grid-content-sticky {
      background-color: ${(props) => grid_sticky_header_bg(props)};
    }

    &:hover .k-grid-content-sticky,
    &.k-state-hover .k-grid-content-sticky {
      background-color: ${(props) => grid_sticky_hovered_bg(props)};
    }
  }

  .k-column-list-item:hover,
  .k-columnmenu-item:hover {
    color: ${(props) => list_item_hovered_text(props)};
    background-color: ${(props) => list_item_hovered_bg(props)};
  }
  .k-columnmenu-item:focus,
  .k-columnmenu-item.k-state-focus {
    color: ${(props) => list_item_focused_shadow(props)};
  }

  .k-columnmenu-item {
    &.k-state-selected {
      color: ${(props) => list_item_selected_text(props)};
      background-color: ${(props) => list_item_selected_bg(props)};
    }
  }

  .k-column-menu {
    .k-menu:not(.k-context-menu) {
      color: ${(props) => popup_text(props)};
      background-color: ${(props) => popup_bg(props)};
      border-color: ${(props) => popup_border(props)};

      .k-item {
        color: ${(props) => list_item_text(props)};
        background-color: ${(props) => list_item_bg(props)};

        &:hover,
        &.k-state-hover {
          color: ${(props) => list_item_hovered_text(props)};
          background-color: ${(props) => list_item_hovered_bg(props)};
        }

        &.k-state-selected {
          color: ${(props) => list_item_selected_text(props)};
          background-color: ${(props) => list_item_selected_bg(props)};
        }

        &:focus,
        &.k-state-focused {
          /* @include box-shadow($list-item-focused-shadow); */
        }
      }
    }
  }

  .k-column-menu-group-header-text {
    color: ${(props) => grid_column_menu_group_header_text(props)};
    background-color: ${(props) => grid_column_menu_group_header_bg(props)};
    border-color: ${(props) => grid_column_menu_group_header_border(props)};
  }

  .k-check-all-wrap {
    border-color: ${(props) => popup_border(props)};
  }

  .k-grid-norecords-template {
    background-color: ${(props) => grid_bg(props)};
    border-color: ${(props) => grid_border(props)};
  }

  .k-pager-wrap {
    background-color: ${(props) => grid_bg(props)};
    color: ${(props) => grid_header_text(props)};
    border-top: none;
  }

  .k-pager-numbers .k-link {
    color: ${(props) => grid_header_text(props)};
    background-color: ${(props) => grid_bg(props)};
  }

  .k-pager-numbers .k-link:focus {
    box-shadow: none;
  }
  .k-pager-numbers .k-link.k-state-selected {
    color: ${(props) => grid_text(props)};

    background-color: ${(props) => props.theme.palette.primary.light};
  }

  .tooltip_icon .k-icon {
    color: ${(props) => grid_text(props)};
  }
  .k-menu-item {
    margin: auto;
  }
`;

export const StyledGridWrapper = styled.div`
  /** 
 * RE-STYLING 
 * */

  .k-grid {
    border-radius: 5px;
    overflow: hidden;
  }
  .k-header {
    background-color: ${(props) => props.theme.palette.secondary.dark};

    > div {
      > div {
        top: 12px;
      }
    }
  }
  .k-grid-header {
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
    overflow: hidden;
    border: 1px solid ${(props) => props.theme.palette.secondary.dark};
  }
  .k-grid-header-sticky {
    background-color: ${(props) => props.theme.palette.secondary.dark};
  }
  .k-grid .k-grid-header-wrap {
    background-color: ${(props) => props.theme.palette.secondary.dark};
  }
  .k-filter-row th,
  .k-grid-header .k-header > .k-link {
    padding: 13px 16px;
  }
  .k-menu-horizontal {
    background-color: transparent;
  }
  .k-menu-horizontal .k-link {
    padding: inherit;
    border: none;
  }
  .tooltip_icon .k-icon {
    color: ${(props) => props.theme.palette.text.primary};
  }
  td.k-grid-content-sticky:last-child,
  th.k-grid-header-sticky:last-child,
  th.k-header:last-child {
    /* border-left: 3px double ${(props) => props.theme.palette.text.secondary};*/
  }
  th.k-header:last-child span {
    /*display: none;*/
  }
  .k-grid-header th.k-grid-header-sticky {
    /*border-left-color: ${(props) => props.theme.palette.text.secondary};*/
  }
  .k-numerictextbox {
    height: 30px;
    overflow: hidden;
  }
  .k-numeric-wrap {
    border-right: none;
    border-top-left-radius: 6px;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 6px;
    overflow: hidden;
  }
  span.k-dropdown-wrap {
    background: ${(props) => props.theme.palette.background.paper};
    border-top: 1px solid ${(props) => props.theme.palette.secondary.dark};
    border-bottom: 1px solid ${(props) => props.theme.palette.secondary.dark};
    border-right: 1px solid ${(props) => props.theme.palette.secondary.dark};
    border-left: 1px solid ${(props) => props.theme.palette.secondary.dark};
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
    border-bottom-left-radius: 6px;
  }
  .k-textbox {
    border-right: none;
    border-top-left-radius: 6px;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 6px;
  }
  .k-filtercell-operator {
    width: 1.9rem;
    margin-left: 0;
    border-top-left-radius: 0;
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
    border-bottom-left-radius: 0;
  }
  .k-menu-link {
    border: 1px solid red;
    border-radius: 4px;
  }
  svg:not(:root).svg-inline--fa,
  svg:not(:host).svg-inline--fa {
    color: ${(props) => props.theme.palette.background.paper};
  }
  .k-pager-wrap {
    border-top: 1px solid ${(props) => props.theme.palette.secondary.dark};
  }
  .k-pager-numbers li {
    border-top: 1px solid ${(props) => props.theme.palette.secondary.dark};
    border-bottom: 1px solid ${(props) => props.theme.palette.secondary.dark};
    border-left: 1px solid ${(props) => props.theme.palette.secondary.dark};
  }
  .k-pager-numbers li:last-child {
    border-right: 1px solid ${(props) => props.theme.palette.secondary.dark};
  }
  .k-pager-numbers .k-link {
    background-color: ${(props) => props.theme.palette.background.paper};
  }
  .k-icon {
    color: ${(props) => props.theme.palette.text.hint};
  }
  th.k-grid-header-sticky {
    vertical-align: middle;
  }
  .k-master-row.k-alt .k-grid-content-sticky,
  table.k-grid-table tbody tr:nth-child(even) td {
    background-color: ${(props) => props.theme.palette.background.default};
  }
  .k-grid tr.k-state-selected > td,
  .k-master-row.k-state-selected .k-grid-content-sticky,
  .k-master-row.k-state-selected.k-alt .gcgrid {
    background-color: ${(props) => props.theme.palette.primary.light};
  }

  .k-textbox:focus-within {
    border-color: ${(props) => props.theme.palette.secondary.dark};
    border-right-color: ${(props) => props.theme.palette.secondary.dark};
    box-shadow: none;
  }
  .k-textbox:focus-visible {
    outline: ${(props) => props.theme.palette.secondary.dark};
    border-width: 1px;
  }

  /* Detail subgrid */
  .k-detail-row .k-hierarchy-cell,
  .k-detail-row .k-detail-cell {
    background-color: #adc3f9 !important;
  }
  .k-detail-row {
    .k-detail-cell {
      .k-grid {
        border-color: #4d78e6;
        border-radius: 0;
        margin-bottom: 0.8rem;
      }
    }
  }
  .k-detail-row {
    .k-detail-cell {
      .k-grid {
        .k-grid-header {
          border-radius: 0;
          border: none;
        }
      }
    }
  }
  .k-detail-row {
    .k-grid-header-wrap {
      border-width: 0px;
    }
  }
  .k-detail-row {
    .k-grid-header-wrap {
      table {
        thead {
          tr > th {
            background-color: #4d78e6;
            color: #fff;
          }
        }
      }
    }
  }
  .k-detail-row {
    .k-grid-header-wrap {
      table {
        thead {
          tr {
            > .k-header span {
              padding-top: 0.2rem;
              padding-bottom: 0.2rem;
            }
          }
        }
      }
    }
  }
  .k-detail-row {
    .k-detail-cell {
      .k-grid {
        .k-grid-container {
          border-radius: 0;
          border: none;
        }
      }
    }
  }
  .k-detail-row {
    .k-detail-cell {
      .k-grid {
        .k-grid-container {
          .k-grid-content {
            div {
              .k-grid-table {
                tbody {
                  tr {
                    td {
                      padding: 5px 12px;
                      color: rgba(0, 0, 0);
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  .k-master-row.k-alt .k-grid-content-sticky:hover,
  .k-grid-table tbody tr:nth-child(2n) td:hover,
  .k-grid-table .k-grid tbody tr:hover td {
    background-color: #e4ebfd;
  }
  .k-button.k-button-icon {
    visibility: visible !important;
    opacity: 0.5;
  }
  .k-filtercell {
    width: calc(100% - 30px);
  }
  .k-button-icon.k-clear-button-visible {
    opacity: 1;
  }
  .k-grid tr.k-state-selected > td {
    color: inherit;
  }

  .k-filtercell-operator span.k-dropdown-wrap {
    border-left: 1px solid ${(props) => props.theme.palette.background.paper};
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  .k-grid,
  .k-grid tr:hover {
    color: inherit !important;
  }
  .k-grid .k-grid-container tr:hover,
  .k-grid .k-grid-container tr:hover td {
    background-color: #e4ebfd;
  }
  .k-grid-table tbody tr:nth-child(2n) td {
    background-color: ${(props) => props.theme.palette.secondary.light};
  }
  .k-detail-row,
  .k-detail-row .detail-grid {
    .k-grid .k-grid-container tr td {
      background-color: #fff;
    }
    .k-grid .k-grid-container tr:hover,
    .k-grid .k-grid-container tr:hover td {
      background-color: #e4ebfd;
    }
  }
  th.k-header {
    padding: 3px 10px !important;
  }
  th.k-header > div > div {
    top: 7px;
  }
`;

export default MyGridCore;
